import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { switchLayout } from '../media-query';
import { ProductSearchQueryArgs } from '../api/models';

export enum Layout {
  PHONE,
  TABLET,
  LAPTOP,
  DESKTOP,
}

export enum PAGE {
  WELCOME,
  SEARCH,
  PRODUCT,
  WATCH_LIST,
  ACCOUNT,
  ABOUT
}

export interface PageVisit {
  page: PAGE,
  url: string,
  query: Record<string, string>;
  params: Record<string, string>;
  productSearchQueryArgs?: ProductSearchQueryArgs;
  [key: string]: any;
}


export interface PagesState {
  visits: PageVisit[];
  layout: Layout;
}

export const initialState: PagesState = {
  visits: [] as PageVisit[],
  layout: Layout.PHONE,
};

function extractProductSearchQueryArgs(pageVisit: PageVisit): ProductSearchQueryArgs {
  const url: URL = new URL(pageVisit.url);
  const urlSearchParams = new URLSearchParams(url.search);

  const productSearchQuery = {} as ProductSearchQueryArgs;

  for (const [key, value] of Object.entries(pageVisit.params)) {
    const paramKey = key.toLowerCase(); // Convert to lowercase for consistency

    if (paramKey === 'q') {
      productSearchQuery.q = value;
    } else if (paramKey === 'limit') {
      productSearchQuery.limit = parseInt(value, 10);
    } else if (paramKey === 'shop_ids') {
      // Handle repeated parameters
      if (!productSearchQuery.shop_ids) {
        productSearchQuery.shop_ids = [];
      }
      productSearchQuery.shop_ids.push(value);
    } else if (paramKey === 'category_ids') {
      // Handle repeated parameters
      if (!productSearchQuery.category_ids) {
        productSearchQuery.category_ids = [];
      }
      productSearchQuery.category_ids.push(decodeURIComponent(value));
    }
  }

  for (const [key, value] of urlSearchParams.entries()) {
    const paramKey = key.toLowerCase(); // Convert to lowercase for consistency

    if (paramKey === 'q') {
      productSearchQuery.q = value;
    } else if (paramKey === 'limit') {
      productSearchQuery.limit = parseInt(value, 10);
    } else if (paramKey === 'shop_ids') {
      // Handle repeated parameters
      if (!productSearchQuery.shop_ids) {
        productSearchQuery.shop_ids = [];
      }
      productSearchQuery.shop_ids.push(value);
    } else if (paramKey === 'category_ids') {
      // Handle repeated parameters
      if (!productSearchQuery.category_ids) {
        productSearchQuery.category_ids = [];
      }
      productSearchQuery.category_ids.push(decodeURIComponent(value));
    }
  }

  return productSearchQuery;
}

export const pagesSlice = createSlice({
  name: 'pages',
  initialState,
  reducers: {
      visit: (state, action: PayloadAction<PageVisit>) => {
        const productSearchQueryArgs: ProductSearchQueryArgs = extractProductSearchQueryArgs(action.payload);
        state.visits.unshift({
          ...action.payload,
          productSearchQueryArgs
        });
      },
    setLayout: (state, action: PayloadAction<Layout>) => {
      state.layout = action.payload;
    },
  },
});
export const { visit, setLayout } = pagesSlice.actions;

export const selectVisits = (state: RootState): PageVisit[] => state.pages.visits || [] as PageVisit[];
export const selectCurrentPage = (state: RootState): PAGE => state.pages.visits.length ? state.pages.visits[0].page : PAGE.WELCOME;
export const selectLayout = (state: RootState): Layout => state.pages.layout;
export const selectAppHeaderHeight = (state: RootState): Layout => switchLayout(state.pages.layout, 50, 60, 65, 75);

export const pagesReducer = pagesSlice.reducer;


export const selectProductSearchQueryArgs = (state: RootState): ProductSearchQueryArgs => state.pages.visits.length && state.pages.visits[0].productSearchQueryArgs ? state.pages.visits[0].productSearchQueryArgs : {};